// 2026-09-24 (user request): Dialog Edit Invoice — dipakai dari tab Invoice &
// Piutang (tombol pensil per baris). Memakai PATCH /active-projects/{pid}/income/{eid}
// (exclude_unset di backend — hanya field yang diisi yang berubah).
import { useState } from "react";
import { X, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { formatIDR } from "@/lib/constants";
import AuditLogButton from "@/components/AuditLogButton";

export default function EditInvoiceDialog({ project, entry, onClose, onSaved }) {
  const [invoiceDate, setInvoiceDate] = useState(entry.invoice_date || "");
  const [amount, setAmount] = useState(String(Math.round(Number(entry.amount) || 0)));
  const [bankName, setBankName] = useState(entry.bank_name || "");
  const [accountNo, setAccountNo] = useState(entry.account_no || "");
  const [pphPct, setPphPct] = useState(entry.pph_pct != null ? String(entry.pph_pct) : "");
  const [followUpDate, setFollowUpDate] = useState(entry.follow_up_date || "");
  const [note, setNote] = useState(entry.note || "");
  const [saving, setSaving] = useState(false);
  const [cancelChecked, setCancelChecked] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const receiptCount = (entry.payment_receipts || []).length;
  const isCancelled = !!entry.is_cancelled;

  // 2026-10-13 (R169): buka kembali invoice yang dibatalkan.
  const reopen = async () => {
    if (!window.confirm("Aktifkan kembali invoice ini? Invoice kembali dihitung dalam piutang (A.R.).")) return;
    try {
      setSaving(true);
      await api.patch(`/active-projects/${project.id}/income/${entry.id}`, { is_cancelled: false });
      toast.success("Invoice diaktifkan kembali — riwayat pembatalan tetap tercatat di audit.");
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Gagal mengaktifkan kembali invoice.");
    } finally { setSaving(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(String(amount).replace(/[^\d]/g, "")) || 0;
    if (amt <= 0) { toast.error("Nilai tagihan harus lebih dari 0."); return; }
    if (receiptCount > 0 && amt !== Math.round(Number(entry.amount) || 0)
        && !window.confirm(`Invoice ini sudah punya ${receiptCount} catatan pembayaran. Mengubah nilai tagihan dapat membuat selisih dengan pembayaran yang sudah tercatat. Lanjutkan?`)) {
      return;
    }
    if (cancelChecked && !isCancelled && !cancelReason.trim()) {
      toast.error("Alasan pembatalan wajib diisi."); return;
    }
    try {
      setSaving(true);
      const body = {
        invoice_date: invoiceDate || null,
        amount: amt,
        bank_name: bankName || null,
        account_no: accountNo || null,
        pph_pct: pphPct === "" ? null : Number(pphPct),
        follow_up_date: followUpDate || null,
        note: note || null,
      };
      if (cancelChecked && !isCancelled) {
        body.is_cancelled = true;
        body.cancelled_reason = cancelReason.trim();
      }
      await api.patch(`/active-projects/${project.id}/income/${entry.id}`, body);
      toast.success(cancelChecked ? "Invoice dibatalkan — dikeluarkan dari perhitungan piutang, tetap tercatat di riwayat." : "Invoice diperbarui.");
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Gagal menyimpan perubahan invoice.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" data-testid="fin-edit-invoice-dialog">
      <form onSubmit={submit} className="bg-white w-full max-w-lg border border-slate-200 shadow-xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div>
            <div className="overline text-[10px] text-slate-500 tracking-widest">EDIT INVOICE</div>
            <div className="font-display text-base font-semibold text-[#0B1D3A]">
              {project?.project_name || project?.name || "Proyek"}{project?.client ? ` · ${project.client}` : ""}
              {isCancelled && <span className="ml-2 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-slate-200 text-slate-600 border border-slate-300" data-testid="fin-edit-cancelled-badge">Dibatalkan</span>}
            </div>
            <div className="text-[10px] text-slate-500">Nilai saat ini: {formatIDR(entry.amount)}{receiptCount > 0 ? ` · ${receiptCount} pembayaran tercatat` : ""}</div>
          </div>
          <AuditLogButton feature_key="invoice" target_id={entry?.id} title={entry?.note || "Invoice"} size="sm" />
          <button type="button" onClick={onClose} className="p-1 hover:bg-slate-100" data-testid="fin-edit-cancel-x"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3 text-[11px]">
          {receiptCount > 0 && (
            <div className="px-3 py-2 bg-amber-50 border border-amber-200 text-amber-800 text-[10px]">
              Perhatian: invoice ini sudah punya catatan pembayaran — perubahan nilai tagihan akan memengaruhi sisa piutang.
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Tanggal Invoice</div>
              <input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} data-testid="fin-edit-date"
                className="w-full border border-slate-300 px-2 py-1.5 text-[11px] bg-white focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]" />
            </div>
            <div>
              <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Nilai Tagihan (Rp) *</div>
              <input inputMode="numeric" value={Number(amount || 0).toLocaleString("id-ID")}
                onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ""))} data-testid="fin-edit-amount"
                className="w-full border border-slate-300 px-2 py-1.5 text-[11px] bg-white text-right tabular-nums focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Bank Tujuan</div>
              <input value={bankName} onChange={(e) => setBankName(e.target.value)} maxLength={60} data-testid="fin-edit-bank"
                className="w-full border border-slate-300 px-2 py-1.5 text-[11px] bg-white focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]" />
            </div>
            <div>
              <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">No Rekening</div>
              <input value={accountNo} onChange={(e) => setAccountNo(e.target.value)} maxLength={40} data-testid="fin-edit-account"
                className="w-full border border-slate-300 px-2 py-1.5 text-[11px] bg-white tabular-nums focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">PPh (%)</div>
              <input inputMode="decimal" value={pphPct} onChange={(e) => setPphPct(e.target.value.replace(/[^\d.]/g, ""))} data-testid="fin-edit-pph"
                className="w-full border border-slate-300 px-2 py-1.5 text-[11px] bg-white text-right tabular-nums focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]" />
            </div>
            <div>
              <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Tanggal Follow-up</div>
              <input type="date" value={followUpDate} onChange={(e) => setFollowUpDate(e.target.value)} data-testid="fin-edit-followup"
                className="w-full border border-slate-300 px-2 py-1.5 text-[11px] bg-white focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]" />
            </div>
          </div>
          <div>
            <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Catatan</div>
            <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} data-testid="fin-edit-note"
              className="w-full border border-slate-300 px-2 py-1.5 text-[11px] bg-white focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]" />
          </div>
          {/* 2026-10-13 (R169): pembatalan invoice + alasan (engine A.R. mengeluarkannya dari perhitungan) */}
          {isCancelled ? (
            <div className="px-3 py-2 bg-slate-100 border border-slate-300 space-y-1" data-testid="fin-edit-cancelled-info">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-600">Invoice dibatalkan</div>
              <div className="text-[10px] text-slate-600">
                Alasan: <span className="font-semibold">{entry.cancelled_reason || "—"}</span>
                {entry.cancelled_by_name && <> · oleh {entry.cancelled_by_name}</>}
                {entry.cancelled_at && <> · {new Date(entry.cancelled_at).toLocaleString("id-ID")}</>}
              </div>
              <div className="text-[9px] text-slate-500 italic">Dikeluarkan dari Outstanding/Jatuh Tempo piutang — tetap tercatat di riwayat & audit.</div>
              <button type="button" onClick={reopen} disabled={saving} data-testid="fin-edit-reopen"
                className="px-2.5 py-1 border border-emerald-300 text-emerald-800 text-[10px] uppercase tracking-wider font-semibold hover:bg-emerald-50 disabled:opacity-50">
                Aktifkan Kembali
              </button>
            </div>
          ) : (
            <div className="px-3 py-2 border border-slate-200 bg-slate-50/60 space-y-1.5" data-testid="fin-edit-cancel-section">
              <label className="flex items-center gap-2 text-[10px] font-semibold text-slate-700 cursor-pointer">
                <input type="checkbox" checked={cancelChecked} onChange={(e) => setCancelChecked(e.target.checked)} data-testid="fin-edit-cancel-check" />
                Batalkan invoice ini
              </label>
              {cancelChecked && (
                <>
                  <input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} maxLength={200}
                    placeholder="Alasan pembatalan (wajib) — mis. invoice ganda, kesalahan kontrak, nota kredit"
                    data-testid="fin-edit-cancel-reason"
                    className="w-full border border-rose-300 px-2 py-1.5 text-[11px] bg-white focus:outline-none focus:ring-1 focus:ring-rose-500" />
                  <div className="text-[9px] text-amber-700 italic">
                    Invoice dibatalkan dikeluarkan dari perhitungan Outstanding & Jatuh Tempo (Rekonsiliasi A.R.) tetapi TIDAK dihapus — riwayat & audit tetap utuh. Pembatalan bisa dibuka kembali.
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-200 bg-slate-50/70">
          <button type="button" onClick={onClose} data-testid="fin-edit-cancel"
            className="px-3 py-1.5 border border-slate-300 text-slate-700 hover:bg-slate-100 text-[10px] uppercase tracking-wider font-semibold">Batal</button>
          <button type="submit" disabled={saving} data-testid="fin-edit-save"
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#0B1D3A] hover:bg-[#12284d] text-white text-[10px] uppercase tracking-wider font-semibold disabled:opacity-50">
            <Save size={12} /> {saving ? "Menyimpan…" : "Simpan Perubahan"}
          </button>
        </div>
      </form>
    </div>
  );
}
